import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { brand, footer, nav } from "../content";

const initialState = { name: "", phone: "", details: "" };

export const ContactFooter = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.details.trim()) {
      toast.error("Please complete every field before submitting.");
      return;
    }
    setSubmitting(true);
    // Simulate a brief submission delay for a premium feel
    await new Promise((r) => setTimeout(r, 700));
    toast.success("Thank you. Our studio will be in touch within 24 hours.");
    setForm(initialState);
    setSubmitting(false);
  };

  return (
    <footer
      id="contact"
      data-testid="contact-footer"
      className="bg-bone border-t border-charcoal/10 pt-24 md:pt-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-16 md:gap-x-12">
          {/* Left: heading & studio info */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-10 h-px bg-taupe" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-taupe">
                Begin a Project
              </span>
            </div>
            <h2
              data-testid="contact-headline"
              className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-[64px] tracking-[-0.02em] leading-[1.05]"
            >
              Tell us about<br />
              <span className="italic text-taupe">the space.</span>
            </h2>
            <p className="mt-6 max-w-md text-charcoal/70 leading-relaxed">
              Share a few details and we’ll respond within one business day with
              a discovery call and an honest assessment.
            </p>

            <div className="mt-12 space-y-5">
              <a
                href={`tel:+${brand.phoneRaw}`}
                data-testid="footer-phone"
                className="flex items-center gap-4 text-charcoal group"
              >
                <Phone size={16} strokeWidth={1.5} className="text-taupe" />
                <span className="text-base group-hover:underline underline-offset-4">
                  {brand.phoneDisplay}
                </span>
              </a>
              <a
                href={`mailto:${brand.email}`}
                data-testid="footer-email"
                className="flex items-center gap-4 text-charcoal group"
              >
                <Mail size={16} strokeWidth={1.5} className="text-taupe" />
                <span className="text-base group-hover:underline underline-offset-4">
                  {brand.email}
                </span>
              </a>
              <div className="flex items-start gap-4 text-charcoal">
                {/* Added shrink-0 below to prevent the icon from squishing */}
                <MapPin size={16} strokeWidth={1.5} className="text-taupe mt-1 shrink-0" />
                <div>
                  <p className="text-base">{footer.studio.address}</p>
                  <p className="text-[12px] text-charcoal/60 mt-1">
                    {footer.studio.hours}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            onSubmit={onSubmit}
            data-testid="lead-form"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="col-span-12 md:col-span-6 md:pl-10 md:border-l border-charcoal/10"
          >
            <div className="space-y-8">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your full name"
                testId="form-name"
              />
              <Field
                label="Phone Number"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={onChange}
                placeholder="+91 00000 00000"
                testId="form-phone"
              />
              <Field
                label="Project Details"
                name="details"
                as="textarea"
                value={form.details}
                onChange={onChange}
                placeholder="Tell us about the space, location, scope and timeline."
                testId="form-details"
              />

              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit-btn"
                className="group inline-flex items-center gap-3 bg-charcoal text-bone px-8 py-4 text-sm tracking-wide hover:bg-charcoal/85 transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Enquiry"}
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>

              <p className="text-[12px] text-charcoal/55 max-w-md">
                By submitting, you agree to receive a one-time response from our
                studio. We never share your details with third parties.
              </p>
            </div>
          </motion.form>
        </div>

        {/* Bottom bar */}
        <div className="mt-24 md:mt-32 pt-10 border-t border-charcoal/10 pb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
            <div>
              <p className="font-serif text-3xl md:text-4xl text-charcoal tracking-tight">
                {brand.name}
              </p>
              <p className="text-[11px] uppercase tracking-[0.3em] text-taupe mt-3 leading-relaxed">
  1st Floor, 110/C, Amherst St, <br />
  College Street Bata, College Row, <br />
  Machuabazar, Kolkata, West Bengal 700009
</p>
            </div>

            <nav className="flex flex-wrap gap-6 md:gap-8" data-testid="footer-nav">
              {nav.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  className="text-sm text-charcoal/80 hover:text-charcoal transition-colors duration-300"
                >
                  {n.label}
                </a>
              ))}
            </nav>

            <div className="flex flex-wrap gap-6">
              {footer.links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-charcoal/80 hover:text-charcoal transition-colors duration-300"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px] text-charcoal/55">
            <p>© {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
            <p>Designed & engineered in-house.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Field = ({ label, name, value, onChange, placeholder, type = "text", as, testId }) => (
  <label className="block group">
    <span className="block text-[11px] uppercase tracking-[0.3em] text-taupe mb-3">
      {label}
    </span>
    {as === "textarea" ? (
      <textarea
        name={name}
        rows={4}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-charcoal/25 focus:border-charcoal outline-none py-2 text-charcoal placeholder:text-charcoal/35 resize-none transition-colors duration-300"
      />
    ) : (
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-charcoal/25 focus:border-charcoal outline-none py-2 text-charcoal placeholder:text-charcoal/35 transition-colors duration-300"
      />
    )}
  </label>
);
